import { useCallback, useEffect, useState } from "react";
import { FileSignature, Loader2, Send, BellRing, FileText, FileCheck2, RefreshCw, FilePlus2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { fmtDT } from "@/constants";

export const CONTRACT_STATUSES = {
  a_preparer: { label: "À préparer", dot: "bg-slate-400", cls: "bg-slate-100 text-slate-700" },
  incomplet: { label: "Informations manquantes", dot: "bg-amber-500", cls: "bg-amber-100 text-amber-800" },
  pret: { label: "Prêt à envoyer", dot: "bg-sky-500", cls: "bg-sky-100 text-sky-800" },
  envoye: { label: "Envoyé — en attente de signature", dot: "bg-amber-500", cls: "bg-amber-100 text-amber-800" },
  en_retard: { label: "Signature en retard", dot: "bg-rose-500", cls: "bg-rose-100 text-rose-700" },
  signe: { label: "Signé", dot: "bg-emerald-500", cls: "bg-emerald-100 text-emerald-700" },
  erreur_envoi: { label: "Échec envoi contrat", dot: "bg-rose-500", cls: "bg-rose-100 text-rose-700" },
  erreur_signature: { label: "Erreur signature électronique", dot: "bg-rose-500", cls: "bg-rose-100 text-rose-700" },
};

export function ContractStatusChip({ status }) {
  const s = CONTRACT_STATUSES[status] || CONTRACT_STATUSES.a_preparer;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold ${s.cls}`} data-testid={`contract-status-${status}`}>
      <span className={`h-2 w-2 rounded-full ${s.dot}`} /> {s.label}
    </span>
  );
}

const DATE_ROWS = [
  ["generated_at", "Contrat généré"],
  ["sent_at", "Envoyé"],
  ["last_reminder_at", "Dernière relance"],
  ["next_reminder_at", "Prochaine relance"],
  ["deadline_label", "Date limite de signature"],
  ["signed_at", "Signé"],
];

export default function ContractSection({ rid }) {
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState("");
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const load = useCallback(() => {
    api.get(`/reservations/${rid}/contract`).then((r) => setC(r.data)).catch(() => toast.error("Contrat : chargement impossible"));
  }, [rid]);
  useEffect(() => { load(); }, [load]);

  const act = async (name, fn, ok) => {
    setBusy(name);
    try {
      await fn();
      if (ok) toast.success(ok);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Action impossible");
      if (e.response?.data?.missing) load();
    } finally {
      setBusy("");
    }
  };

  // Régénère (prepare / new-version) puis propose immédiatement l'envoi en signature si le contrat est prêt
  const prepareThenSend = async (kind) => {
    const busyLabel = kind === "new-version" ? "newver" : kind;
    setBusy(busyLabel);
    try {
      await api.post(`/reservations/${rid}/contract/${kind}`);
      const fresh = (await api.get(`/reservations/${rid}/contract`)).data;
      setC(fresh);
      toast.success(kind === "new-version"
        ? "Nouvelle version créée — le contrat indique qu'il remplace et annule les versions antérieures"
        : "Contrat généré");
      if (fresh.display_status === "pret" && window.confirm("Contrat prêt. L'envoyer maintenant pour signature au client ?")) {
        setBusy("send");
        await api.post(`/reservations/${rid}/contract/send`);
        toast.success("Contrat envoyé pour signature");
        load();
      }
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Action impossible");
      if (e.response?.data?.missing) load();
    } finally {
      setBusy("");
    }
  };

  if (!c) return null;
  const st = c.display_status;
  const canSend = st === "pret";
  const canRemind = st === "envoye" || st === "en_retard";

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-4" data-testid="contract-section">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="font-heading font-bold flex items-center gap-2">
          <FileSignature className="h-5 w-5 text-slate-500" /> Contrat de location
        </h2>
        <span className="flex items-center gap-2 flex-wrap">
          {c.version > 1 && (
            <span className="rounded-full bg-violet-100 text-violet-700 px-2.5 py-1 text-xs font-bold" data-testid="contract-version-badge">
              Version {c.version} — remplace les versions antérieures
            </span>
          )}
          <ContractStatusChip status={st} />
        </span>
      </div>

      {st === "incomplet" && c.missing?.length > 0 && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-3.5 space-y-2" data-testid="contract-missing">
          <p className="text-sm font-bold text-amber-900">⚠️ Contrat non envoyé — informations à compléter</p>
          <ul className="text-sm text-amber-900 list-disc pl-5">
            {c.missing.map((m) => <li key={m} data-testid={`contract-missing-${m}`}>{m}</li>)}
          </ul>
          {c.missing.includes("Créneau de reprise") && (
            <div className="flex flex-wrap gap-2 pt-1" data-testid="contract-pickup-quickset">
              {["11h30 – 12h00", "13h00 – 13h30", "18h00 – 18h30"].map((s) => (
                <Button key={s} size="sm" variant="outline" data-testid={`contract-pickup-${s.slice(0, 2)}`}
                  disabled={busy === "slot"}
                  onClick={() => act("slot", () => api.post(`/reservations/${rid}/contract/pickup-slot`, { slot: s }), `Reprise définie : ${s}`)}>
                  {s}
                </Button>
              ))}
              <Button size="sm" variant="outline" data-testid="contract-pickup-other" disabled={busy === "slot"}
                onClick={() => {
                  const v = window.prompt("Créneau de reprise (ex. 16h00 – 16h30) :");
                  if (v) act("slot", () => api.post(`/reservations/${rid}/contract/pickup-slot`, { slot: v }), "Reprise définie");
                }}>
                Autre
              </Button>
            </div>
          )}
        </div>
      )}

      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm" data-testid="contract-dates">
        {DATE_ROWS.map(([k, label]) => (
          <div key={k} className="flex justify-between gap-2">
            <dt className="text-slate-500">{label}</dt>
            <dd className="font-medium text-right" data-testid={`contract-date-${k}`}>{c[k] ? (k === "deadline_label" ? c[k] : fmtDT(c[k])) : "—"}</dd>
          </div>
        ))}
      </dl>

      {c.signing_url && canRemind && (
        <div className="rounded-xl border border-sky-200 bg-sky-50 p-3 space-y-2" data-testid="contract-signing-link">
          <p className="text-xs text-sky-900 font-semibold">
            Lien de signature du client — à renvoyer par SMS si l'e-mail DocuSeal n'arrive pas (vérifiez aussi ses courriers indésirables) :
          </p>
          <div className="flex gap-2">
            <input readOnly value={c.signing_url} data-testid="contract-signing-url"
              className="flex-1 h-9 rounded-lg border border-slate-200 bg-white px-2.5 text-xs font-mono" />
            <Button size="sm" variant="outline" data-testid="contract-copy-link"
              onClick={() => { navigator.clipboard?.writeText(c.signing_url); toast.success("Lien de signature copié"); }}>
              Copier
            </Button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {(st === "a_preparer" || st === "incomplet" || st === "pret") && (
          <Button size="sm" variant="outline" data-testid="contract-prepare" disabled={!!busy}
            onClick={() => prepareThenSend("prepare")}>
            {busy === "prepare" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            {st === "a_preparer" ? "Préparer le contrat" : "Regénérer le contrat"}
          </Button>
        )}
        {c.has_pdf && (
          <a href={`${API_BASE}/reservations/${rid}/contract/pdf`} target="_blank" rel="noreferrer"
            data-testid="contract-view-pdf"
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 h-8 text-sm font-medium text-sky-700 hover:border-sky-400 transition-colors">
            <FileText className="h-4 w-4" /> Voir le contrat
          </a>
        )}
        {canSend && (
          <Button size="sm" data-testid="contract-send" disabled={!!busy}
            className="bg-sky-500 hover:bg-sky-600 text-white transition-colors"
            onClick={() => act("send", () => api.post(`/reservations/${rid}/contract/send`), "Contrat envoyé pour signature")}>
            {busy === "send" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Envoyer pour signature
          </Button>
        )}
        {canRemind && (
          <Button size="sm" variant="outline" data-testid="contract-remind" disabled={!!busy}
            onClick={() => act("remind", () => api.post(`/reservations/${rid}/contract/remind`), "Relance envoyée (copie Photobooth54)")}>
            {busy === "remind" ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellRing className="h-4 w-4" />}
            Relancer maintenant
          </Button>
        )}
        {(st === "erreur_envoi" || st === "erreur_signature") && (
          <Button size="sm" data-testid="contract-retry" disabled={!!busy}
            className="bg-rose-600 hover:bg-rose-700 text-white transition-colors"
            onClick={() => act("send", () => api.post(`/reservations/${rid}/contract/send`), "Contrat renvoyé")}>
            {busy === "send" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Renvoyer le contrat
          </Button>
        )}
        {(st === "envoye" || st === "en_retard" || st === "signe") && (
          <Button size="sm" variant="outline" data-testid="contract-new-version" disabled={!!busy}
            onClick={() => {
              if (window.confirm("Créer une nouvelle version du contrat ? La version actuelle sera archivée et le nouveau contrat indiquera qu'il remplace et annule toute version antérieure."))
                prepareThenSend("new-version");
            }}>
            {busy === "newver" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FilePlus2 className="h-4 w-4" />}
            Nouvelle version (remplace l'ancienne)
          </Button>
        )}
        {st === "signe" && c.has_signed_pdf && (
          <a href={`${API_BASE}/reservations/${rid}/contract/signed-pdf`} target="_blank" rel="noreferrer"
            data-testid="contract-view-signed"
            className="inline-flex items-center gap-1.5 rounded-md border border-emerald-300 bg-emerald-50 px-3 h-8 text-sm font-medium text-emerald-700 transition-colors">
            <FileCheck2 className="h-4 w-4" /> Contrat signé (PDF + preuve)
          </a>
        )}
        {st !== "signe" && !confirmingDelete && (
          <Button size="sm" variant="ghost" data-testid="contract-delete" disabled={!!busy}
            className="text-rose-600 hover:bg-rose-50"
            onClick={() => setConfirmingDelete(true)}>
            <Trash2 className="h-4 w-4" /> Supprimer
          </Button>
        )}
      </div>

      {confirmingDelete && (
        <div className="rounded-xl border-2 border-rose-400 bg-rose-50 p-3.5 space-y-2" data-testid="contract-delete-confirm">
          <p className="text-sm font-bold text-rose-800">Supprimer définitivement ce contrat ? Cette action est irréversible.</p>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" data-testid="contract-delete-confirm-yes" disabled={!!busy}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              onClick={() => act("delete", async () => { await api.delete(`/reservations/${rid}/contract`); setConfirmingDelete(false); }, "Contrat supprimé définitivement")}>
              {busy === "delete" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Oui, supprimer définitivement
            </Button>
            <Button size="sm" variant="outline" data-testid="contract-delete-confirm-no" onClick={() => setConfirmingDelete(false)}>Annuler</Button>
          </div>
        </div>
      )}

      {(c.history || []).length > 0 && (
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Historique du contrat</p>
          <ul className="space-y-1">
            {c.history.slice().reverse().map((h, i) => (
              <li key={i} data-testid={`contract-history-${i}`} className="text-sm text-slate-600">
                <span className="font-medium">{fmtDT(h.at)}</span> — {h.label}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
